import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCustomPlanNavigation, getCustomPlanDefaultPage, isRouteAllowedForCustomPlan } from '@/hooks/useCustomPlanNavigation';
import { useAuth } from '@/hooks/useAuth';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';

interface CustomPlanRedirectProps {
  children: React.ReactNode;
}

// Routes that are always accessible regardless of subscription status
const ALWAYS_ALLOWED_ROUTES = [
  '/settings',
  '/settings/subscription',
  '/documentation',
  '/profile',
  '/choose-plan',
  '/subscription/custom',
  '/subscription/checkout',
];

/**
 * Component that handles routing based on subscription status:
 * 1. Redirects users without valid subscriptions to subscription page
 * 2. Redirects custom plan users to their allowed features
 * 3. Prevents access to paid features without valid subscription
 */
export function CustomPlanRedirect({ children }: CustomPlanRedirectProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { isLoading: isLoadingCustomPlan, isCustomPlan, featureKeys } = useCustomPlanNavigation();
  const { 
    isLoading: isLoadingSubscription, 
    accessLevel, 
    requiresPayment,
    hasValidPaidSubscription 
  } = useSubscriptionAccess();

  const isLoading = isLoadingCustomPlan || isLoadingSubscription;

  useEffect(() => {
    // Don't do anything while loading or if not authenticated
    if (isLoading || !isAuthenticated) return;

    const currentPath = location.pathname;

    // Check if current route is always allowed
    const isAlwaysAllowed = ALWAYS_ALLOWED_ROUTES.some(route => 
      currentPath === route || currentPath.startsWith(route + '/')
    );

    // If user requires payment (no subscription, pending, expired)
    // and they're trying to access a restricted route
    if (requiresPayment && !isAlwaysAllowed) {
      console.log(`[CustomPlanRedirect] User requires payment, redirecting from ${currentPath} to /settings/subscription`);
      navigate('/settings/subscription', { replace: true });
      return;
    }

    // For users with subscriptions disabled, allow full access
    if (accessLevel === 'subscriptions_disabled') return;

    // For free tier users, they can access basic features (handled by feature flags)
    if (accessLevel === 'free') return;

    // Only apply custom plan redirects for valid custom plan users
    if (!isCustomPlan || featureKeys.length === 0) return;

    // Check if the current route is allowed for this user's feature set
    if (!isRouteAllowedForCustomPlan(currentPath, featureKeys)) {
      // Redirect to their default page
      const defaultPage = getCustomPlanDefaultPage(featureKeys);
      console.log(`[CustomPlanRedirect] User doesn't have access to ${currentPath}, redirecting to ${defaultPage}`);
      navigate(defaultPage, { replace: true });
      return;
    }

    // Special case: If user lands on /dashboard but doesn't have dashboard feature
    if (currentPath === '/dashboard' && !featureKeys.includes('dashboard') && !featureKeys.includes('advanced_dashboard')) {
      const defaultPage = getCustomPlanDefaultPage(featureKeys);
      if (defaultPage !== '/dashboard') {
        console.log(`[CustomPlanRedirect] No dashboard access, redirecting from /dashboard to ${defaultPage}`);
        navigate(defaultPage, { replace: true });
      }
    }

    // Special case: If user lands on /settings directly (e.g., after login), redirect to their default feature page
    // But allow access if they navigated there intentionally (has referrer within app)
    if (currentPath === '/settings' && !location.state?.fromNav) {
      const defaultPage = getCustomPlanDefaultPage(featureKeys);
      // Only redirect if there's a real feature page to go to (not /settings/subscription fallback)
      if (defaultPage !== '/settings/subscription' && defaultPage !== '/settings') {
        console.log(`[CustomPlanRedirect] Redirecting from /settings to default page ${defaultPage}`);
        navigate(defaultPage, { replace: true });
      }
    }
  }, [isLoading, isAuthenticated, isCustomPlan, featureKeys, location.pathname, navigate, requiresPayment, accessLevel]);

  return <>{children}</>;
}
